import { Button, DropdownButton, Menu } from '@affine/component';
import { BlockCard } from '@affine/component/card/block-card';
import { createStressDoc } from '@affine/core/modules/cabinet';
import { DocsService } from '@affine/core/modules/doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import {
  EdgelessIcon,
  ImportIcon,
  PageIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import type { MouseEvent, PropsWithChildren } from 'react';
import { useCallback, useState } from 'react';

import * as styles from './new-page-button.css';

type NewPageButtonProps = {
  createNewDoc: (e?: MouseEvent) => void;
  createNewPage: (e?: MouseEvent) => void;
  createNewEdgeless: (e?: MouseEvent) => void;
  importFile?: () => void;
  size?: 'small' | 'default';
  closeMenu?: () => void;
};

export const CreateNewPagePopup = ({
  createNewPage,
  createNewEdgeless,
  importFile,
  closeMenu,
}: NewPageButtonProps) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '8px',
        }}
      >
        <BlockCard
          title={t['com.affine.new.page-mode']()}
          desc={t['com.affine.write_with_a_blank_page']()}
          right={<PageIcon width={20} height={20} />}
          onClick={createNewPage}
          onAuxClick={createNewPage}
          data-testid="new-page-button-in-all-page"
        />
        <BlockCard
          title={t['com.affine.new_edgeless']()}
          desc={t['com.affine.draw_with_a_blank_whiteboard']()}
          right={<EdgelessIcon width={20} height={20} />}
          onClick={createNewEdgeless}
          onAuxClick={createNewEdgeless}
          data-testid="new-edgeless-button-in-all-page"
        />
        <BlockCard
          title="Шаблоны"
          desc="Смета, тайминг, гости и другие готовые страницы"
          right={<TemplateIcon width={20} height={20} />}
          onClick={() => {
            closeMenu?.();
            workbench.open('/templates');
          }}
          data-testid="open-templates-gallery"
        />
        {location.hostname === 'localhost' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 4px',
            }}
          >
            <Button
              onClick={() => {
                closeMenu?.();
                createStressDoc(docsService)
                  .then(id => {
                    const ws =
                      location.pathname.match(/\/workspace\/([^/]+)/)?.[1];
                    if (ws) {
                      location.href = `/workspace/${ws}/${id}`;
                    } else {
                      workbench.openDoc(id);
                    }
                  })
                  .catch(console.error);
              }}
              data-testid="create-stress-doc"
            >
              Стресс
            </Button>
          </div>
        ) : null}
        {importFile ? (
          <BlockCard
            title={t['com.affine.new_import']()}
            desc={t['com.affine.import_file']()}
            right={<ImportIcon width={20} height={20} />}
            onClick={importFile}
            data-testid="import-button-in-all-page"
          />
        ) : null}
        {/* TODO Import */}
      </div>
    </>
  );
};

export const NewPageButton = ({
  createNewDoc,
  createNewPage,
  createNewEdgeless,
  importFile,
  size,
  children,
}: PropsWithChildren<NewPageButtonProps>) => {
  const [open, setOpen] = useState(false);

  const handleCreateNewDoc: NewPageButtonProps['createNewDoc'] = useCallback(
    e => {
      createNewDoc(e);
      setOpen(false);
    },
    [createNewDoc]
  );

  const handleCreateNewPage: NewPageButtonProps['createNewPage'] = useCallback(
    e => {
      createNewPage(e);
      setOpen(false);
    },
    [createNewPage]
  );

  const handleCreateNewEdgeless: NewPageButtonProps['createNewEdgeless'] =
    useCallback(
      e => {
        createNewEdgeless(e);
        setOpen(false);
      },
      [createNewEdgeless]
    );

  const closeMenu = useCallback(() => setOpen(false), []);

  const handleImportFile = useCallback(() => {
    importFile?.();
    setOpen(false);
  }, [importFile]);

  return (
    <>
      <Menu
        items={
          <CreateNewPagePopup
            createNewDoc={handleCreateNewDoc}
            createNewPage={handleCreateNewPage}
            createNewEdgeless={handleCreateNewEdgeless}
            importFile={importFile ? handleImportFile : undefined}
            closeMenu={closeMenu}
          />
        }
        rootOptions={{
          open,
        }}
        contentOptions={{
          className: styles.menuContent,
          align: 'end',
          hideWhenDetached: true,
          onInteractOutside: useCallback(() => {
            setOpen(false);
          }, []),
        }}
      >
        <DropdownButton
          size={size}
          onClick={handleCreateNewDoc}
          onAuxClick={handleCreateNewPage}
          onClickDropDown={useCallback(() => setOpen(open => !open), [])}
          className={styles.button}
        >
          {children}
        </DropdownButton>
      </Menu>
    </>
  );
};
